import TagsInput from 'react-tagsinput';
import ModalComponent from 'components/Common/Modal';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import useTranscripts from 'api/hooks/useTranscripts';
import { store } from 'components/Store';
import createNotification from 'components/Settings/Utilities/CreateNotification';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreateTranscriptModal = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state ?? {};

  const [emailAddressesSelected, setEmailAddressesSelected] = useState(
    team?.myself?.emailAddress ? [team?.myself.emailAddress] : []
  );
  const [emailError, setEmailError] = useState('');
  const [loadingCreation, setLoadingCreation] = useState(false);
  //const [notifyEmails, setNotifyEmails] = useState(true);
  const inputRef = useRef(null);

  const { openState, activeDebateState, setTranscripts } = props;
  const { isOpen, setIsOpen } = openState ?? {};
  const { activeDebate, setActiveDebate } = activeDebateState ?? {};
  const { debateTitle, title, eventTitle, status, existingTranscript, id } = activeDebate ?? {};
  const { createTranscript, updateTranscript } = useTranscripts();
  const finishedTranscript = existingTranscript && status === 'Emailed';
  const originalEmails = activeDebate?.notifyEmails ?? [];
  const recipientsUnchanged =
    emailAddressesSelected.length === originalEmails.length &&
    [...emailAddressesSelected].sort().join(',') === [...originalEmails].sort().join(',');

  const navigate = useNavigate();
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    setEmailAddressesSelected(
      existingTranscript
        ? (activeDebate?.notifyEmails ?? [])
        : team?.myself?.emailAddress
          ? [team?.myself.emailAddress]
          : []
    );
  }, [activeDebate]);

  return (
    <ModalComponent
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onAfterClose={() => {
        setActiveDebate(null);
        setEmailAddressesSelected(team?.myself?.emailAddress ? [team?.myself.emailAddress] : []);
        setEmailError('');
        setLoadingCreation(false);
        //setNotifyEmails(true);
      }}
      maxWidth={750}
    >
      <div className='border-bottom px-5 py-4'>
        <p className='font-weight-bold mb-0 title-h5-m'>
          {existingTranscript ? 'Your transcript request' : 'Create a new transcript and report'}
        </p>
        <p className='light-60-text mb-0'>{debateTitle ?? title ?? eventTitle}</p>
      </div>
      <div className='px-5 specific-top-padding-28'>
        <div className='d-flex'>
          {/* <div className='checkbox-item checkbox-item-empty d-flex mt-1'>
            <input
              id={`send-email`}
              onChange={(e) => {
                setNotifyEmails(e.target.checked);
              }}
              type={'checkbox'}
              checked={notifyEmails}
            />
            <label htmlFor={`send-email`} className='create-email-alert-label flex-grow-1'></label>
          </div> */}
          <div className={`flex-grow-1`}>
            <p className='mb-0 font-weight-bold'>Receive an email notification when ready</p>
            <p className='light-60-text'>Select recipients below. Transcripts will also appear on the platform.</p>
            <div
              className={`rounded border px-2 py-1 transcripts-recipients-modal d-flex flex-column justify-content-center${finishedTranscript ? ' transcripts-recipients-modal-disabled' : ''}`}
              style={{ minHeight: '51px' }}
            >
              <TagsInput
                value={emailAddressesSelected}
                disabled={finishedTranscript}
                onChange={(emails) => {
                  if (emails.length < emailAddressesSelected.length) {
                    setTimeout(() => inputRef.current?.focus(), 0);
                  }
                  setEmailAddressesSelected(emails);
                  setEmailError('');
                }}
                onlyUnique
                addOnBlur
                renderLayout={(tagComponents, inputComponent) => (
                  <div className='d-flex flex-wrap align-items-center'>
                    {tagComponents}
                    <span className='flex-grow-1'>{inputComponent}</span>
                  </div>
                )}
                validationRegex={EMAIL_REGEX}
                onValidationReject={() => setEmailError('Please enter a valid email address')}
                inputProps={{
                  placeholder: emailAddressesSelected.length === 0 ? 'Type an email and press Enter...' : '',
                  ref: inputRef,
                  className: 'react-tagsinput-input w-100',
                  autoComplete: 'off',
                }}
              />
            </div>

            {emailError && <p className='text-danger paragraph-p3 mt-2 mb-0'>{emailError}</p>}
          </div>
        </div>
      </div>

      <div className='p-4 px-lg-5'>
        <div className='d-flex justify-content-end py-3'>
          <button
            className='rounded-pill action-button general-button px-4 py-2'
            disabled={loadingCreation || (existingTranscript && !finishedTranscript && recipientsUnchanged)}
            onClick={async () => {
              if (finishedTranscript) {
                setIsOpen(false);
              } else {
                setLoadingCreation(true);
                try {
                  const { parliamentEventId, rollingHansardContentExternalId } = activeDebate ?? {};

                  if (existingTranscript) {
                    const request = {
                      id,
                      notifyEmails: emailAddressesSelected,
                    };

                    const { status } = await updateTranscript(request);
                    if (status === 200) {
                      createNotification('success', `Transcript updated successfully`);
                      setTranscripts((prevTranscripts) =>
                        prevTranscripts.map((transcript) =>
                          transcript.id === id ? { ...transcript, notifyEmails: emailAddressesSelected } : transcript
                        )
                      );
                    }
                    setLoadingCreation(false);
                    setIsOpen(false);
                  } else {
                    let request = {
                      notifyEmails: emailAddressesSelected,
                    };
                    if (parliamentEventId) {
                      request = {
                        ...request,
                        parliamentEventId: parliamentEventId,
                      };
                    }
                    if (rollingHansardContentExternalId) {
                      request = {
                        ...request,
                        rollingHansardContentExternalId: rollingHansardContentExternalId,
                      };
                    }
                    const { status } = await createTranscript(request);
                    if (status === 200) {
                      createNotification('success', `Transcript scheduled successfully`);
                    }
                    setLoadingCreation(false);
                    setIsOpen(false);
                    navigate(`/monitor-inbox/transcripts/your-transcripts`);
                  }
                } catch (error) {
                  setLoadingCreation(false);
                }
              }
            }}
          >
            {loadingCreation && (
              <i className='fas fa-spinner fa-spin specific-right-margin-10 d-inline-block-centered' />
            )}
            {`${existingTranscript ? `${finishedTranscript ? 'Close' : 'Confirm recipients'}` : 'Create transcript'} `}
          </button>
        </div>
      </div>
    </ModalComponent>
  );
};

export default CreateTranscriptModal;
